import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useParams } from 'react-router-dom';
import { caseStudies, getCaseStudyBySlug } from '../lib/caseStudiesData';
import CtaBanner from '../components/home/CtaBanner';

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const project = getCaseStudyBySlug(slug);

  if (!project) return <Navigate to="/case-studies" replace />;

  const currentIndex = caseStudies.findIndex((item) => item.slug === slug);
  const nextProject = caseStudies[(currentIndex + 1) % caseStudies.length];
  const pageUrl = `https://www.nexgeius.com/case-studies/${project.slug}`;
  const imageUrl = `https://www.nexgeius.com${project.image}`;

  return (
    <>
      <Helmet>
        <title>{project.name} Case Study | Nexgeius</title>
        <meta name="description" content={project.summary} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${project.name} Case Study | Nexgeius`} />
        <meta property="og:description" content={project.summary} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${project.name}: ${project.headline}`,
          description: project.summary,
          image: imageUrl,
          author: { '@type': 'Organization', name: 'Nexgeius', url: 'https://www.nexgeius.com/' },
          publisher: { '@type': 'Organization', name: 'Nexgeius', url: 'https://www.nexgeius.com/' },
          mainEntityOfPage: pageUrl,
        })}</script>
      </Helmet>

      <main>
        <section className="page-grid bg-[#F6F8FA] px-6 pb-16 pt-28 lg:px-8 lg:pb-20 lg:pt-32">
          <div className="relative z-10 mx-auto max-w-7xl">
            <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm font-semibold text-[#52636E] transition hover:text-[#176B8A]">
              <span aria-hidden="true">&larr;</span> All work
            </Link>
            <div className="mt-10 grid gap-9 lg:grid-cols-[1.15fr_.85fr] lg:items-end">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full border border-[#C7D8DF] bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: project.accent }}>{project.category}</span>
                  <span className="text-xs font-medium text-[#82939C]">{project.year}</span>
                </div>
                <h1 className="mt-6 max-w-4xl text-[clamp(2.7rem,6vw,5.5rem)] font-semibold leading-[0.97] tracking-[-0.055em] text-[#102A3A]">{project.headline}</h1>
              </div>
              <div className="max-w-xl lg:justify-self-end">
                <p className="text-lg leading-8 text-[#52636E]">{project.summary}</p>
                <a
                  href={project.url}
                  target="_blank"
                  rel="noreferrer"
                  className="button-primary mt-7 inline-flex items-center gap-2"
                >
                  Visit live project <span aria-hidden="true">&nearr;</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#F6F8FA] px-6 pb-20 lg:px-8 lg:pb-28">
          <div className="mx-auto max-w-7xl overflow-hidden rounded-[1.75rem] border border-[#CFDBE0] bg-[#E7EEF1] shadow-[0_25px_80px_rgba(16,42,58,0.12)]">
            <img src={project.image} alt={`${project.name} website`} width="1440" height="900" className="aspect-[16/9] w-full object-cover object-top" />
          </div>
        </section>

        <section className="border-y border-[#DCE4E8] bg-white px-6 py-7 lg:px-8">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3">
            <span className="mr-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#82939C]">The work</span>
            {project.services.map((service) => (
              <span key={service} className="rounded-full bg-[#EEF3F5] px-3 py-1.5 text-xs font-medium text-[#334D5B]">{service}</span>
            ))}
          </div>
        </section>

        <section className="bg-white px-6 py-20 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-12 border-b border-[#DCE4E8] pb-16 lg:grid-cols-[.35fr_.65fr]">
              <div>
                <p className="section-kicker">The problem</p>
                <h2 className="mt-4 text-3xl font-semibold tracking-[-0.035em] text-[#102A3A]">What the experience needed to solve.</h2>
              </div>
              <p className="max-w-3xl text-xl leading-9 tracking-[-0.015em] text-[#52636E]">{project.problem}</p>
            </div>

            <div className="grid gap-12 border-b border-[#DCE4E8] py-16 lg:grid-cols-[.35fr_.65fr]">
              <div>
                <p className="section-kicker">The approach</p>
                <h2 className="mt-4 text-3xl font-semibold tracking-[-0.035em] text-[#102A3A]">Make the next action feel obvious.</h2>
              </div>
              <div>
                <p className="max-w-3xl text-lg leading-8 text-[#52636E]">{project.approach}</p>
                <ul className="mt-9 grid gap-3 sm:grid-cols-2">
                  {project.delivered.map((item) => (
                    <li key={item} className="flex gap-3 rounded-xl bg-[#F6F8FA] p-4 text-sm font-medium leading-6 text-[#253E4D]">
                      <span className="mt-1 text-[#176B8A]" aria-hidden="true">&#10003;</span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="grid gap-12 pt-16 lg:grid-cols-[.35fr_.65fr]">
              <div>
                <p className="section-kicker">The outcome</p>
                <h2 className="mt-4 text-3xl font-semibold tracking-[-0.035em] text-[#102A3A]">A stronger working foundation.</h2>
              </div>
              <div>
                <p className="max-w-3xl text-xl leading-9 tracking-[-0.015em] text-[#52636E]">{project.outcome}</p>
                <p className="mt-7 text-sm leading-6 text-[#82939C]">We only publish performance numbers when a client has supplied and approved them. This case study focuses on the work that can be verified directly.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#102A3A] px-6 py-16 text-white lg:px-8">
          <Link to={`/case-studies/${nextProject.slug}`} className="group mx-auto grid max-w-7xl gap-7 lg:grid-cols-[.7fr_1.3fr_auto] lg:items-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#92C5D8]">Next case study</p>
            <div>
              <p className="text-3xl font-semibold tracking-[-0.035em]">{nextProject.name}</p>
              <p className="mt-2 text-sm text-[#C5D3DA]">{nextProject.category}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/25 transition group-hover:translate-x-1 group-hover:bg-white group-hover:text-[#102A3A]" aria-hidden="true">&rarr;</span>
          </Link>
        </section>

        <CtaBanner />
      </main>
    </>
  );
};

export default CaseStudyDetail;
